import {
  Create,
  SimpleForm,
  DateInput,
  TextInput,
  ReferenceInput,
  AutocompleteInput,
  required,
  minLength,
  maxLength,
} from "react-admin";
import { TextField as MuiTextField } from "@mui/material";
import { useGetOne } from "react-admin";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Col, Row } from "../../components/grid.component";

const validateTrackingCode = [required(), minLength(3), maxLength(255)];
const validatePlugNumber = [required(), minLength(3), maxLength(255)];
const validateDate = [required()];

const getLicenseIdFromUrl = (): string | null => {
  const searchParams = new URLSearchParams(window.location.search);
  const searchLicenseId = searchParams.get('license_id');
  if (searchLicenseId) {
    return searchLicenseId;
  }

  // React Admin uses hash routing by default; query params may live in the hash fragment.
  const hash = window.location.hash || '';
  const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  if (!hashQuery) {
    return null;
  }

  const hashParams = new URLSearchParams(hashQuery);
  return hashParams.get('license_id');
};

const ReadOnlyField = ({ label, value }: { label: string; value: string | number }) => (
  <MuiTextField
    label={label}
    value={value ?? ''}
    disabled
    fullWidth
    variant="filled"
  />
);

const LicenseMetadataFields = ({ licenseId }: { licenseId: string }) => {
  const { setValue } = useFormContext();
  const { data: license, isLoading: isLoadingLicense } = useGetOne(
    'licenses',
    { id: licenseId },
    { enabled: !!licenseId }
  );

  const userId = license?.userId ?? license?.user?.id;
  const productId = license?.productId ?? license?.product?.id;

  const { data: user, isLoading: isLoadingUser } = useGetOne(
    'users',
    { id: userId },
    { enabled: !!userId }
  );

  const { data: product, isLoading: isLoadingProduct } = useGetOne(
    'products',
    { id: productId },
    { enabled: !!productId }
  );

  useEffect(() => {
    if (licenseId) {
      setValue('license_id', licenseId, { shouldDirty: false });
    }
  }, [licenseId, setValue]);

  useEffect(() => {
    if (productId) {
      setValue('product_id', productId, { shouldDirty: false });
    }
  }, [productId, setValue]);

  return (
    <>
      <Row spacing={3}>
        <Col sm={6}>
          <ReadOnlyField label="Usuário" value={isLoadingUser ? 'Carregando...' : (user?.name || '')} />
        </Col>
        <Col sm={6}>
          <ReadOnlyField label="Produto" value={isLoadingProduct ? 'Carregando...' : (product?.name || '')} />
        </Col>
      </Row>
      <Row spacing={3}>
        <Col sm={6}>
          <ReadOnlyField label="Código do serial" value={isLoadingLicense ? 'Carregando...' : (license?.serialNumber || '')} />
        </Col>
        <Col sm={6}>
          <ReadOnlyField label="Versão" value={isLoadingLicense ? 'Carregando...' : (license?.version ?? '')} />
        </Col>
      </Row>
    </>
  );
};

const SelectedLicenseMetadata = () => {
  const { control } = useFormContext();
  const licenseId = useWatch({ control, name: 'license_id' });

  if (!licenseId) {
    return null;
  }

  return <LicenseMetadataFields licenseId={licenseId} />;
};

const LicenseSelector = () => {
  return (
    <ReferenceInput
      source="license_id"
      reference="licenses"
      label="Licença"
    >
      <AutocompleteInput
        fullWidth
        optionText={(record: any) =>
          `${record.serialNumber} (${record.user?.name || "N/A"})`
        }
        filterToQuery={(searchText: string) => ({ serialNumber: searchText })}
      />
    </ReferenceInput>
  );
};

export const ShipmentCreate = () => {
  const licenseId = getLicenseIdFromUrl();
  const { data: selectedLicense } = useGetOne(
    'licenses',
    { id: licenseId || '' },
    { enabled: !!licenseId }
  );
  const selectedProductId = selectedLicense?.productId ?? selectedLicense?.product?.id;

  return (
    <Create
      transform={(data: any) => ({
        ...data,
        ...(licenseId && { license_id: licenseId }),
        ...(selectedProductId && { product_id: selectedProductId }),
      })}
    >
      <SimpleForm>
        {licenseId && <LicenseMetadataFields licenseId={licenseId} />}

        {!licenseId && (
          <Row spacing={3}>
            <Col sm={4}>
              <LicenseSelector />
            </Col>
            <Col sm={4}>
              <DateInput
                source="shipment_date"
                label="Data da remessa"
                validate={validateDate}
                defaultValue={new Date().toISOString().split("T")[0]}
                fullWidth
              />
            </Col>
            <Col sm={4}>
              <DateInput
                source="receipt_date"
                label="Data de Recebimento (Opcional)"
                helperText="Data em que a remessa foi recebida. Deixe em branco se ainda não foi recebida."
                fullWidth
              />
            </Col>
          </Row>
        )}

        {!licenseId && <SelectedLicenseMetadata />}

        <Row spacing={3}>
          <Col sm={6}>
            <TextInput
              source="tracking_code"
              label="Código de rastreamento"
              validate={validateTrackingCode}
              helperText="Código de rastreamento único para esta remessa"
              fullWidth
            />
          </Col>
          <Col sm={6}>
            <TextInput
              source="plug_number"
              label="Número do Plug"
              validate={validatePlugNumber}
              helperText="Número de série do dispositivo/plug (ex.: SDA900000)"
              fullWidth
            />
          </Col>
        </Row>

        {licenseId && (
          <Row spacing={3}>
            <Col sm={6}>
              <DateInput
                source="shipment_date"
                label="Data da remessa"
                validate={validateDate}
                defaultValue={new Date().toISOString().split("T")[0]}
                fullWidth
              />
            </Col>
            <Col sm={6}>
              <DateInput
                source="receipt_date"
                label="Data de Recebimento (Opcional)"
                helperText="Data em que a remessa foi recebida. Deixe em branco se ainda não foi recebida."
                fullWidth
              />
            </Col>
          </Row>
        )}
      </SimpleForm>
    </Create>
  );
};
